/**
 * Opening records and screens from anywhere (design `api.open` / `api.go`): people, projects,
 * clients and entities open their cards; a contract opens on its party's «Договор» tab.
 */
import { useNavigate } from '@tanstack/react-router';
import { useCallback } from 'react';
import { NAV_PATHS } from '../components/shell/nav';
import type { ScreenKey } from '../components/shell/nav';
import { api } from './api';

export type RecordKind = 'person' | 'project' | 'client' | 'contract' | 'entity';

const CARD_PATHS = {
  person: '/people/$id',
  project: '/projects/$id',
  client: '/clients/$id',
  entity: '/entities/$id',
} as const;

interface ContractParty {
  readonly kind: 'client' | 'contractor';
  readonly clientId: string | null;
  readonly personId: string | null;
}

export function useOpenRecord(): (kind: RecordKind, id: string) => void {
  const navigate = useNavigate();
  return useCallback(
    (kind, id) => {
      if (kind !== 'contract') {
        void navigate({ to: CARD_PATHS[kind], params: { id } });
        return;
      }
      void api.get<ContractParty>(`/contracts/${encodeURIComponent(id)}`).then((contract) => {
        const to = contract.kind === 'client' ? CARD_PATHS.client : CARD_PATHS.person;
        const party = contract.kind === 'client' ? contract.clientId : contract.personId;
        if (party) void navigate({ to, params: { id: party }, search: { tab: 'contract', contract: id } as never });
      });
    },
    [navigate],
  );
}

export function useGoScreen(): (screen: ScreenKey, search?: Record<string, string>) => void {
  const navigate = useNavigate();
  return useCallback(
    (screen, search) => void navigate({ to: NAV_PATHS[screen], search: (search ?? {}) as never }),
    [navigate],
  );
}
